import { useAuth0 } from '@auth0/auth0-react'
import { connect } from '@cerebral/react'
import { Apps, ArrowDropDown, ChevronLeft, ChevronRight } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { AppBar, Box, Button, Collapse, IconButton, Link, MenuItem, SvgIcon, Toolbar, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { props, sequences, state } from 'cerebral'
import throttle from 'lodash.throttle'
import { clone, indexBy, isEmpty, keys, omit, prop, values } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Menu as MenuIcon } from 'react-feather'
import { SizeMe } from 'react-sizeme'
import { useRegisterSW } from 'virtual:pwa-register/react'
import apiClient from '../../lib/util/api-client'
import brand from '../../lib/util/brand'
import { omitConnectProps } from '../../lib/util/cerebral'
import { useToggleState } from '../../lib/util/hooks'
import MetricCardsBarCollapsed from '../blocks/metric-cards/MetricCardsBarCollapsed'
import MetricCardsBarExpanded from '../blocks/metric-cards/MetricCardsBarExpanded'
import MetricCardsGrid from '../blocks/metric-cards/MetricCardsGrid'
import MetricSetup from '../blocks/metric-cards/MetricSetup'
import DropDown from '../controls/DropDown'
import TrialNotice from '../elements/TrialNotice'
import UserAvatar from '../elements/UserAvatar'

const Logo = brand.logo
const isQuiltt = brand.name === 'Quiltt'

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    '@media print': {
      display: 'none',
    },
  },
  toolbar: {
    height: 64,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: theme.palette.grey['200'],
  },
  logo: {
    height: 36,
    marginRight: theme.spacing(2),
  },
  menuButton: {
    marginLeft: -12, // Offset for button padding.
  },
  wrapperInner: {
    maxWidth: '100%',
  },
  toggleCardsButton: {
    borderRight: `0.5px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    alignItems: 'center',
  },
  metricCardsDesktop: {
    flex: 1,
    display: 'none',
    justifyContent: 'flex-end',
    margin: theme.spacing(0, 2),
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  mobileOnly: {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  mobileOnlySpacer: {
    flex: 1,
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}))

function getNestedResorts(resorts) {
  const nestedResorts = {}
  const resortEntities = indexBy(prop('_id'), resorts)
  const resortIds = keys(resortEntities)

  for (const resort of resorts) {
    const { _id, parent = {} } = resort

    // Resort's parent must exist among granted resorts for this resort to be nested
    if (parent && parent._id && resortIds.includes(parent?._id.toString())) {
      if (!resortEntities[parent?._id].resorts) {
        resortEntities[parent?._id].resorts = []
      }
      resortEntities[parent?._id].resorts.push(resortEntities[_id])
    } else {
      nestedResorts[_id] = resortEntities[_id]
    }
  }

  return values(nestedResorts)
}

const TitleForDropdown = connect(
  {
    type: state`account.${props`type`}`,
  },

  (props) => {
    const { type, showCaret = true, ...rest } = props
    return type ? (
      <Box display="flex" alignItems="center">
        <Typography variant="h4" {...omit(['get', 'reaction'], rest)}>
          {type.name}
        </Typography>
        {showCaret && <ArrowDropDown />}
      </Box>
    ) : null
  }
)

const ResortMenuItem = connect(
  {
    changeCalendar: sequences`calendars.changeCalendar`,
    changeMealLocation: sequences`mealLocations.changeMealLocation`,
    changeOrganization: sequences`account.changeOrganization`,
    setEventCalendars: sequences`events.setEventCalendars`,
    organization: state`account.organization`,
  },
  ({ changeCalendar, changeMealLocation, changeOrganization, setEventCalendars, organization = {}, id, name, slug, resorts = [] }) => {
    return (
      <>
        <MenuItem
          onClick={() => {
            setEventCalendars({ value: [''] })
            changeCalendar(null)
            changeMealLocation(null)
            changeOrganization({ orgSlug: slug })
          }}
        >
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" flex="1">
            <Box>{name}</Box>
            <Box pl={1} visibility={organization._id === id ? 'visible' : 'hidden'}>
              <CheckIcon />
            </Box>
          </Box>
        </MenuItem>

        {!isEmpty(resorts) ? (
          <Box marginLeft={2}>
            {resorts.map(({ _id: resortId, name: resortName, slug: resortSlug, resorts: resortsList = [] } = {}) => (
              <ResortMenuItem key={resortSlug} id={resortId} name={resortName} slug={resortSlug} resorts={resortsList} />
            ))}
          </Box>
        ) : null}
      </>
    )
  }
)

const HandleServiceWorker = connect(
  {
    updateAvailable: state`ui.updateAvailable`,
    setUpdateAvailable: sequences`ui.setUpdateAvailable`,
  },
  ({ updateAvailable, setUpdateAvailable }) => {
    // Service worker configuration from vite-plugin-pwa.
    const { updateServiceWorker } = useRegisterSW({
      onNeedRefresh: setUpdateAvailable,

      onRegistered: (swRegistered) => {
        const checkServiceWorker = () => {
          if (swRegistered) {
            swRegistered.update()
          }
        }

        const throttledCheckServiceWorker = throttle(checkServiceWorker, 5 * 60 * 1000, { leading: true, trailing: true })

        // Check for updates when focusing window.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            throttledCheckServiceWorker()
          }
        })

        apiClient.io.on('connect', checkServiceWorker)
      },
    })

    const updateNow = useCallback(() => updateServiceWorker(true), [updateServiceWorker])

    return updateAvailable ? (
      <Box mx={2}>
        <Button onClick={updateNow}>Reload now to update?</Button>
      </Box>
    ) : null
  }
)

const TopBar = connect(
  {
    user: state`account.currentUser`,
    userChecked: state`account.checked`,
    organization: state`account.organization`,
    signOut: sequences`account.signOut`,
    mobileNavOpen: state`ui.mobileNavOpen`,
    setMobileNavOpen: sequences`ui.setMobileNavOpen`,
    metricGridMobileOpen: state`ui.metricGridMobileOpen`,
    setMetricGridMobileOpen: sequences`ui.setMetricGridMobileOpen`,
    metricCardsExpanded: state`ui.metricCardsExpanded`,
    toggleMetricCardsExpanded: sequences`ui.toggleMetricCardsExpanded`,
    metrics: state`analyticsLive.metrics`,
    navShown: state`route.navShown`,
    tenant: state`account.tenant`,
    toLogin: sequences`route.toLogin`,
  },

  (props) => ({ ...props, signOut: () => props.signOut() }),

  ({
    user,
    userChecked,
    organization,
    setMobileNavOpen,
    mobileNavOpen,
    metricGridMobileOpen,
    setMetricGridMobileOpen,
    metricCardsExpanded,
    toggleMetricCardsExpanded,
    metrics,
    signOut,
    tenant,
    navShown,
    toLogin,
    ...rest
  }) => {
    rest = omitConnectProps(rest)
    const classes = useStyles()
    const resortsList = useMemo(() => (organization ? getNestedResorts(clone(user.resorts)) : []), [user, organization])
    const [gridOpen, openGrid, closeGrid] = useToggleState(false)
    const gridAnchorRef = useRef(null)

    const { logout: logoutAuth0 } = useAuth0()

    const logOutCompletely = useCallback(async () => {
      await signOut()
      logoutAuth0({ logoutParams: { returnTo: window.location.origin, clientId: tenant.clientId } })
    }, [signOut, logoutAuth0])

    // If a user without access to Command Center reaches an internal view, redirect them to finish logging in.
    useEffect(() => {
      if (navShown && userChecked && !user?.canAccessCommandCenter) {
        toLogin()
      }
    }, [navShown, user, userChecked])

    const toggleMetricCards = useCallback(() => toggleMetricCardsExpanded(), [])
    const toggleMetricGrid = useCallback(() => setMetricGridMobileOpen({ open: !metricGridMobileOpen }), [metricGridMobileOpen])
    const closeMetricGrid = useCallback(() => setMetricGridMobileOpen({ open: false }), [])

    return navShown ? (
      <AppBar elevation={0} className={classes.root} color="default" {...rest}>
        <Toolbar className={classes.toolbar}>
          <Box sx={{ display: { lg: 'none', xs: 'block' } }}>
            <IconButton className={classes.menuButton} color="inherit" onClick={() => setMobileNavOpen({ open: !mobileNavOpen })} size="large">
              <SvgIcon fontSize="small">
                <MenuIcon />
              </SvgIcon>
            </IconButton>
          </Box>

          <Box sx={{ display: { lg: 'block', xs: 'none' } }}>
            <Logo className={classes.logo} />
          </Box>

          {organization &&
            (resortsList < 2 ? (
              <TitleForDropdown showCaret={false} type={'organization'} />
            ) : (
              <DropDown
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                component={TitleForDropdown}
                componentProps={{ type: 'organization' }}
              >
                {resortsList.map(({ _id: id, name, slug, resorts = [] } = {}) => (
                  <Box key={slug} display="flex" flexDirection="column" flex="1">
                    <ResortMenuItem id={id} name={name} slug={slug} resorts={resorts} />
                  </Box>
                ))}
              </DropDown>
            ))}

          {isQuiltt && <MetricSetup />}

          {isQuiltt && metrics.length > 0 ? (
            <SizeMe>
              {({ size }) => {
                const roomForMetrics = Math.floor(size.width / 241)
                const tooManyMetrics = roomForMetrics < metrics.length

                return (
                  <>
                    <Box className={classes.metricCardsDesktop}>
                      <Box className={classes.toggleCardsButton}>
                        <IconButton onClick={toggleMetricCards} size="small">
                          {metricCardsExpanded ? <ChevronRight /> : <ChevronLeft />}
                        </IconButton>
                      </Box>

                      <Collapse
                        in={metricCardsExpanded}
                        orientation="horizontal"
                        timeout={250}
                        mountOnEnter
                        unmountOnExit
                        classes={{ wrapperInner: classes.wrapperInner }}
                      >
                        <MetricCardsBarExpanded metrics={metrics.slice(0, roomForMetrics)} resortId={organization?._id} />
                      </Collapse>
                      <Collapse in={!metricCardsExpanded || tooManyMetrics} orientation="horizontal" timeout={250}>
                        <MetricCardsBarCollapsed onGridOpen={openGrid} gridAnchorRef={gridAnchorRef} />
                      </Collapse>
                      <MetricCardsGrid
                        open={gridOpen}
                        onClose={closeGrid}
                        metrics={metrics}
                        resortId={organization?._id}
                        isMobile={false}
                        anchorEl={gridAnchorRef.current}
                      />
                    </Box>

                    <Box className={classes.mobileOnlySpacer} />

                    <Box className={classes.mobileOnly}>
                      <IconButton color="inherit" onClick={toggleMetricGrid} size="large">
                        <Apps fontSize="small" />
                      </IconButton>
                    </Box>
                    <MetricCardsGrid open={metricGridMobileOpen} onClose={closeMetricGrid} metrics={metrics} resortId={organization?._id} isMobile />
                  </>
                )
              }}
            </SizeMe>
          ) : (
            <Box flexGrow={1} />
          )}

          <HandleServiceWorker />

          <TrialNotice />

          {user && (
            <>
              <DropDown
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                component={UserAvatar}
              >
                <MenuItem>
                  <Link color="textPrimary" underline="none" href={`mailto:${brand.email}`} target="_blank" rel="noopener noreferrer">
                    Email Support
                  </Link>
                </MenuItem>
                <MenuItem>
                  <Link color="textPrimary" underline="none" href="https://alpinemediatech.com/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                </MenuItem>

                <MenuItem onClick={logOutCompletely}>Log Out</MenuItem>
              </DropDown>
            </>
          )}
        </Toolbar>
      </AppBar>
    ) : null
  }
)

export default TopBar
